import type Mark from 'mark.js'
import type { Dispatch, RefCallback, SetStateAction } from 'react'
import type { EFormFieldMapping } from '~/features/preChatForms/models'

export enum EChannelKeys {
  phone = 'phone',
  email = 'email',
  text = 'text',
  chat = 'chatbot',
}

export type InboxListEntry = {
  agentId: string
  // Null for a chatbot conversation that hasn't been matched/attached to a
  // Contact yet (see the Coalesce Contact ticket) — the visitor hasn't
  // provided a phone/email, or heyLibbyChat's live coalescing hasn't run
  // yet. guestId identifies it instead until that happens.
  contactId: string | null
  guestId: string | null
  name: string | null
  firstName: string | null
  lastName: string | null
  email: string | null
  phone: string | null
  conversationId: string
  lastActivity: string
  // Null for a contact-less row (see contactId above) — the backend only
  // tracks a real read/unread flag per Contact once one exists, so this is
  // never populated for a chatbot conversation still awaiting coalescing.
  // conversationRead below is Conversation.read, the equivalent for that
  // case — see contactlessRowToInboxContact / rawContactToInboxContact in
  // crm-service's contactService.ts.
  contactRead: boolean | null
  conversationRead: boolean | null | undefined
  latestConversationSummary: string
  latestConversationChannel: EChannelKeys
}

export type TFetchInboxListResponse = {
  data: InboxListEntry[]
  // Opaque keyset pagination cursor from the backend (see
  // encodeInboxCursor/decodeInboxCursor in replifyCRM) — null means this was
  // the last page. Replaces the old page-number scheme: merging
  // contact-rooted and contact-less conversations into one ordered list
  // requires keyset pagination, not offset.
  nextCursor: string | null
}

export enum EInboxFiltersKeys {
  status = 'status',
  agentId = 'agentId',
  topics = 'topics',
  startDate = 'startDate',
  endDate = 'endDate',
  channel = 'channel',
  direction = 'direction',
  campaignId = 'campaignId',
}

export enum EInboxDirection {
  inbound = 'inbound',
  outbound = 'outbound',
}

export type TInboxFilters = {
  [EInboxFiltersKeys.status]: EInboxFiltersStatus
  [EInboxFiltersKeys.agentId]: string
  [EInboxFiltersKeys.topics]: string[]
  [EInboxFiltersKeys.startDate]: string | null
  [EInboxFiltersKeys.endDate]: string | null
  [EInboxFiltersKeys.channel]: EChannelKeys | null
  [EInboxFiltersKeys.direction]: EInboxDirection | null
  [EInboxFiltersKeys.campaignId]: string | null
}

export type TChatSearchMatchResults = {
  current: number
  total: number
}

export type TInboxChatSearchContentSignature = {
  contentKey: string
  contactId: string
}

export type TInboxWorkspaceContext = {
  searchQuery: string
  setSearchQuery: Dispatch<SetStateAction<string>>
  containerRef: RefCallback<HTMLDivElement | null>
  instance: Mark | null
  results: TChatSearchMatchResults
  setResults: Dispatch<SetStateAction<TChatSearchMatchResults>>
  scrollToSearchMatchIndex: (index: number) => void
  setChatSearchContent: (signature: TInboxChatSearchContentSignature) => void
  filters: TInboxFilters
  setFilters: Dispatch<SetStateAction<TInboxFilters>>
  advancedFiltersDefaultExpanded: boolean
  /** True while ChatSection is resolving a conversationId/messageId deep
   * link. InboxListSection defers its own contact-locating walk until this
   * clears, so the two don't fire competing bursts of page-fetch requests
   * at once on a fresh deep-link load. */
  isChatFocusResolving: boolean
  setIsChatFocusResolving: Dispatch<SetStateAction<boolean>>
  /**
   * True while the currently-open conversation's thread is scrolled near
   * the bottom (mirrors ChatContents' own isNearBottom). InboxItem combines
   * this with its own isFocused check so the currently-open, actively-being-
   * viewed conversation never flashes bold when a new message arrives —
   * the server always flips read=false on any new guest message regardless
   * of whether staff is watching live, and the mark-as-read PUT that
   * corrects it is an async round trip that can visibly lose the race
   * against a refetch. Suppressing bold client-side for this one case
   * sidesteps that race entirely instead of trying to win it. Deliberately
   * NOT suppressed when the open conversation is scrolled away from the
   * bottom — staff hasn't actually seen a message arriving off-screen, so
   * it should still show unread until they scroll down to it.
   */
  isViewingLatestMessages: boolean
  setIsViewingLatestMessages: Dispatch<SetStateAction<boolean>>
  /**
   * Live "guest is typing" / "AI is responding" state for CONTACT-attached
   * conversations, keyed by contactId — populated by useInboxConversationsSocket
   * (the agent-wide staff socket), which is mounted once here rather than
   * per-open-conversation, and is the only live channel that reaches a
   * contact-attached conversation at all (see that hook's own doc comment).
   * A contact-LESS conversation's typing state comes from a separate,
   * per-conversation hook instead (useConversationSocket) — this map only
   * ever has entries for contactId, never for a bare conversationId.
   */
  typingByContactId: Record<
    string,
    { isGuestTyping: boolean; isAssistantTyping: boolean }
  >
}

export enum EInboxFiltersStatus {
  all = 'all',
  unread = 'unread',
  read = 'read',
}

export enum ESpeaker {
  assistant = 'assistant',
  user = 'user',
  system = 'system',
}

export enum EDisplayBoxType {
  conversationSummary = 'conversationSummary',
  emailMessage = 'emailMessage',
  chatMessage = 'chatMessage',
  phoneMessage = 'phoneMessage',
  textMessage = 'textMessage',
  userMessage = 'userMessage',
  phoneRecording = 'phoneRecording',
  formSubmission = 'formSubmission',
}

export type TFormSubmissionField = {
  displayName: string
  value: unknown
  sortOrder: number
  fieldMapping: EFormFieldMapping
}

export type TPagination = {
  nextCursor: string | null
  hasMoreNext: boolean
  previousCursor: string | null
  hasMorePrevious: boolean
}

type TBaseData = {
  id: string
  channel: EChannelKeys
  agentId: string
  agentReferenceName: string
}

export type TDisplayBoxConversationSummaryData = TBaseData & {
  summary: string
  source?: string | null
  leftVoicemail: boolean
  sentText: boolean
  campaignName: string
  campaignId?: string
  customAnalysisFields?: Record<string, unknown> | null
}

export enum EDisplayBoxMessageType {
  toolResponse = 'tool_response',
}

export type TDisplayBoxMessageData = TBaseData & {
  messageText: string
  speaker: ESpeaker
  phone?: string
  email?: string
  emailFromAddress?: string
  emailFromName?: string
  emailToAddress?: string
  emailToName?: string
  subject?: string
  type?: EDisplayBoxMessageType
  details?: string
  status?: EMessageStatus
  statusTimestamp?: string
  vectorData?: unknown
}

export type TDisplayBoxPhoneRecordingData = TBaseData & {
  recordingLink: string
  durationSeconds?: number | null
}

export type TDisplayBoxFormSubmissionData = TBaseData & {
  formName: string
  fields: TFormSubmissionField[]
}

export type TDisplayBoxData =
  | TDisplayBoxMessageData
  | TDisplayBoxConversationSummaryData
  | TDisplayBoxPhoneRecordingData
  | TDisplayBoxFormSubmissionData

export type TDisplayBox = {
  id: string
  type: EDisplayBoxType
  activityDate: string
  data: TDisplayBoxData
}

export type TInboxFeed = {
  data: TDisplayBox[]
  pagination: TPagination
  // The conversation's CURRENT contactId, echoed back by the feed endpoint —
  // present so a contact-less conversation being viewed live (fetched by
  // conversationId) can detect the moment heyLibbyChat's live contact
  // coalescing attaches a contact, and "promote" the view to the real
  // contact thread. Null while still contact-less.
  resolvedContactId: string | null
  // The conversation's guestId, echoed back by the feed endpoint — only ever
  // populated by heyLibbyChat (v2), so this distinguishes a genuine v2
  // contact-less conversation from an old/abandoned v1 one before opening a
  // live WS watch (useConversationSocket) that heyLibbyChat has no knowledge
  // of otherwise. Null on the normal contactId path and for v1 conversations.
  resolvedGuestId: string | null
}

export enum EInboxActiveView {
  inbox = 'inbox',
  chat = 'chat',
  accountDetails = 'accountDetails',
}

export enum EInboxParams {
  contactId = 'contactId',
  conversationId = 'conversationId',
}

export enum EMessageStatus {
  Bounced = 'Bounced',
  Complained = 'Complained',
  Delivered = 'Delivered',
  Sent = 'Sent',
  Rejected = 'Rejected',
  Opened = 'Opened',
  Clicked = 'Clicked',
}

export type TToolCallLogMessage = {
  functionName?: string
  finalizedUrl?: string
  parameters?: Record<string, string>
  httpMethod?: string
  responseCode?: number
  responseBody?: string
  errorCode?: string
  errorBody?: string
  timestampIso?: string
}

export enum EUnsubscribeAction {
  unsubscribe = 'unsubscribe',
  resubscribe = 'subscribe',
}
